import type { Degree, Distance, Point } from "@pdfking/math"
import { PI, Scale, Subtract, Total } from "@pdfking/math"
import { MM } from "@pdfking/quantity"
import type { PathPoint } from "@pdfking/renderer-api"
import { ArcPoints, DrawPath } from "@pdfking/renderer-api"

import type { FolderPath } from "#public"

/** A pen drawing a die's path a stroke at a time, in mm, x right and y down,
 * as a die is drawn: straight to a point, round a quadratic's control to it,
 * or along an arc round a centre from one angle to another, in radians. */
export type FolderPen = {
  line: (x: number, y: number) => void
  quad: (x: number, y: number, cx: number, cy: number) => void
  arc: (cx: number, cy: number, radius: number, from: number, to: number) => void
  path: (closed: boolean) => FolderPath
}

const at = (x: number, y: number): Point<Distance> => ({ x: MM(x), y: MM(y) })

// Two thirds of the way from `p` to `c`: a quadratic's control as a cubic's
// handle
const handle = (p: Point<Distance>, c: Point<Distance>): Point<Distance> => ({
  x: Total(p.x, Scale(Subtract(c.x, p.x), 2 / 3)),
  y: Total(p.y, Scale(Subtract(c.y, p.y), 2 / 3)),
})

const degrees = (radians: number) => ((radians * 180) / PI) as Degree

/** A pen with nothing drawn yet. */
export const FolderPen = (): FolderPen => {
  const points: PathPoint[] = []
  return {
    // Straight to a point; nothing to where the pen already is
    line: (x, y) => {
      const [point, last] = [at(x, y), points[points.length - 1]]
      if (last && last.x === point.x && last.y === point.y) return
      points.push(...DrawPath([point]))
    },
    quad: (x, y, cx, cy) => {
      const [control, anchor] = [at(cx, cy), at(x, y)]
      const last = points.pop()!
      points.push(
        { ...last, after: handle(last, control) },
        { ...anchor, before: handle(anchor, control), after: anchor }
      )
    },
    arc: (cx, cy, radius, from, to) => {
      points.push(...ArcPoints(at(cx, cy), MM(radius), degrees(from), degrees(to - from)))
    },
    path: closed => ({ points: [...points], closed }),
  }
}
